// Accès aux données réelles : Supabase (PostgreSQL + règles d’accès par utilisateur + temps réel).
import { createClient, type SupabaseClient } from '@supabase/supabase-js';
import { cleanLegacyNote, validateBookingSettings, type Booking, type BookingSettings } from '../core/booking';
import { validateContact, validateProfile, type Contact, type Profile } from '../core/contact';
import { describeError, RepoError, type LiveStatus, type Repo, type RepoEvent, type Snapshot } from './repo';

export function makeSupabaseClient(): SupabaseClient {
  const url = import.meta.env.VITE_SUPABASE_URL, key = import.meta.env.VITE_SUPABASE_KEY;
  if (!url || !key) throw new Error('Configuration serveur absente (VITE_SUPABASE_URL / VITE_SUPABASE_KEY).');
  // Flux « implicit » : le lien reçu par e-mail fonctionne dans n’importe quel navigateur de l’appareil
  // (l’appli installée et Chrome partagent la session sur Android). Le flux PKCE exigerait d’ouvrir
  // le lien exactement dans le navigateur qui l’a demandé.
  return createClient(url, key, {
    auth: { flowType: 'implicit', persistSession: true, autoRefreshToken: true, detectSessionInUrl: true },
  });
}

interface ContactRow {
  id: string;
  org: string;
  name: string;
  tz: string;
  place: string;
  country: string;
  role: string;
  start_min: number;
  end_min: number;
  days: number[];
  color: string;
  favorite: boolean;
  created_at: string;
  updated_at: string;
  deleted_at: string | null;
}

interface ProfileRow {
  user_id?: string;
  tz: string;
  place: string;
  country: string;
  start_min: number;
  end_min: number;
  days: number[];
  display_name: string;
  reply_email: string;
  theme: string;
  sort: string;
}

function contactFromRow(r: ContactRow): Contact {
  return validateContact({
    id: r.id, org: r.org, name: r.name, tz: r.tz, place: r.place, country: r.country, role: r.role,
    start: r.start_min, end: r.end_min, days: r.days, color: r.color, favorite: r.favorite,
    createdAt: r.created_at, updatedAt: r.updated_at, deletedAt: r.deleted_at,
  });
}

function contactToRow(c: Contact): Omit<ContactRow, 'created_at' | 'updated_at'> {
  return {
    id: c.id, org: c.org, name: c.name, tz: c.tz, place: c.place, country: c.country, role: c.role,
    start_min: c.start, end_min: c.end, days: c.days, color: c.color, favorite: c.favorite, deleted_at: c.deletedAt,
  };
}

function profileFromRow(r: ProfileRow): Profile {
  return validateProfile({
    tz: r.tz, place: r.place, country: r.country, start: r.start_min, end: r.end_min, days: r.days,
    displayName: r.display_name, replyEmail: r.reply_email, theme: r.theme, sort: r.sort,
  });
}

function profileToRow(p: Profile): ProfileRow {
  return {
    tz: p.tz, place: p.place, country: p.country, start_min: p.start, end_min: p.end, days: p.days,
    display_name: p.displayName, reply_email: p.replyEmail, theme: p.theme, sort: p.sort,
  };
}

interface BookingSettingsRow {
  user_id?: string;
  enabled: boolean;
  duration_min: number;
  buffer_min: number;
  notice_hours: number;
  horizon_days: number;
  windows: unknown;
  title: string;
  intro_fr: string;
  intro_en: string;
  require_approval?: boolean;
}

function bookingSettingsFromRow(r: BookingSettingsRow): BookingSettings {
  return validateBookingSettings({ enabled: r.enabled, duration: r.duration_min, buffer: r.buffer_min, noticeHours: r.notice_hours, horizonDays: r.horizon_days, windows: r.windows, title: r.title, introFr: r.intro_fr, introEn: r.intro_en, requireApproval: r.require_approval });
}

function bookingSettingsToRow(s: BookingSettings): BookingSettingsRow {
  return { enabled: s.enabled, duration_min: s.duration, buffer_min: s.buffer, notice_hours: s.noticeHours, horizon_days: s.horizonDays, windows: s.windows, title: s.title, intro_fr: s.introFr, intro_en: s.introEn, require_approval: s.requireApproval };
}

interface BookingRow {
  id: string;
  start_at: string;
  end_at: string;
  name: string;
  email: string;
  note: string;
  tz: string;
  lang: string;
  status: string;
  created_at: string;
  meet_url: string | null;
  google_event_id?: string | null;
  guests?: string[] | null;
}

const BOOKING_STATUSES = ['confirmed', 'pending', 'declined', 'cancelled'] as const;

function bookingFromRow(r: BookingRow): Booking {
  const status = (BOOKING_STATUSES as readonly string[]).includes(r.status) ? (r.status as Booking['status']) : 'confirmed';
  return {
    id: r.id, startAt: Date.parse(r.start_at), endAt: Date.parse(r.end_at), name: r.name, email: r.email, note: cleanLegacyNote(r.note ?? ''),
    tz: r.tz, lang: r.lang === 'en' ? 'en' : 'fr', status, createdAt: r.created_at, meetUrl: r.meet_url ?? null,
    googleEventId: r.google_event_id ?? null,
    guests: Array.isArray(r.guests) ? r.guests.filter((g): g is string => typeof g === 'string') : [],
  };
}

export class SupabaseRepo implements Repo {
  constructor(private readonly client: SupabaseClient, private readonly userId: () => string | null) {}

  private uid(): string {
    const id = this.userId();
    if (!id) throw new RepoError('Session expirée : reconnectez-vous.');
    return id;
  }

  async loadAll(): Promise<Snapshot> {
    try {
      const uid = this.uid();
      const since = new Date(Date.now() - 7 * 86_400_000).toISOString();
      const [c, p, bs, bk] = await Promise.all([
        this.client.from('contacts').select('*').order('created_at', { ascending: true }),
        this.client.from('profiles').select('*').eq('user_id', uid).maybeSingle(),
        this.client.from('booking_settings').select('*').eq('user_id', uid).maybeSingle(),
        this.client.from('bookings').select('*').gte('end_at', since).order('start_at', { ascending: true }),
      ]);
      if (c.error) throw c.error;
      if (p.error) throw p.error;
      // Les tables de rendez-vous peuvent ne pas exister encore (migration 0002 non appliquée) : on continue sans elles.
      const bookingMissing = !!bs.error || !!bk.error;
      if (bookingMissing) console.warn('Tables de rendez-vous indisponibles', bs.error?.message ?? bk.error?.message);
      const contacts: Contact[] = [];
      for (const row of (c.data ?? []) as ContactRow[]) {
        try { contacts.push(contactFromRow(row)); } catch (e) { console.warn('Contact illisible ignoré', row.id, e); }
      }
      const bookings: Booking[] = [];
      for (const row of (bk.data ?? []) as BookingRow[]) {
        try { bookings.push(bookingFromRow(row)); } catch (e) { console.warn('Rendez-vous illisible ignoré', row.id, e); }
      }
      return {
        profile: p.data ? profileFromRow(p.data as ProfileRow) : null, contacts, readAt: Date.now(),
        booking: bs.data ? bookingSettingsFromRow(bs.data as BookingSettingsRow) : null, bookings,
      };
    } catch (e) { throw describeError(e); }
  }

  async saveContact(c: Contact): Promise<Contact> {
    try {
      const { data, error } = await this.client.from('contacts').upsert(contactToRow(c), { onConflict: 'id' }).select('*').single();
      if (error) throw error;
      return contactFromRow(data as ContactRow);
    } catch (e) { throw describeError(e); }
  }

  private async patch(id: string, patch: Partial<ContactRow>): Promise<Contact> {
    try {
      const { data, error } = await this.client.from('contacts').update(patch).eq('id', id).select('*').single();
      if (error) throw error;
      return contactFromRow(data as ContactRow);
    } catch (e) { throw describeError(e); }
  }

  deleteContact(id: string, deletedAt: string): Promise<Contact> { return this.patch(id, { deleted_at: deletedAt }); }
  restoreContact(id: string): Promise<Contact> { return this.patch(id, { deleted_at: null }); }

  async purgeContact(id: string): Promise<void> {
    try {
      const { error } = await this.client.from('contacts').delete().eq('id', id);
      if (error) throw error;
    } catch (e) { throw describeError(e); }
  }

  async saveProfile(p: Profile): Promise<Profile> {
    try {
      const row = { ...profileToRow(p), user_id: this.uid() };
      const { data, error } = await this.client.from('profiles').upsert(row, { onConflict: 'user_id' }).select('*').single();
      if (error) throw error;
      return profileFromRow(data as ProfileRow);
    } catch (e) { throw describeError(e); }
  }

  async saveBookingSettings(s: BookingSettings): Promise<BookingSettings> {
    try {
      const row = { ...bookingSettingsToRow(s), user_id: this.uid() };
      let res = await this.client.from('booking_settings').upsert(row, { onConflict: 'user_id' }).select('*').single();
      if (res.error?.message?.includes('require_approval')) {
        // Migration 0005 pas encore appliquée : la colonne n’existe pas encore. Le reste s’enregistre quand
        // même ; la validation manuelle ne prendra effet qu’une fois le SQL exécuté.
        const { require_approval: _drop, ...fallback } = row;
        res = await this.client.from('booking_settings').upsert(fallback, { onConflict: 'user_id' }).select('*').single();
      }
      if (res.error) throw res.error;
      return bookingSettingsFromRow(res.data as BookingSettingsRow);
    } catch (e) { throw describeError(e); }
  }

  async cancelBooking(id: string): Promise<Booking> {
    try {
      const { data, error } = await this.client.from('bookings').update({ status: 'cancelled', cancelled_at: new Date().toISOString() }).eq('id', id).select('*').single();
      if (error) throw error;
      return bookingFromRow(data as BookingRow);
    } catch (e) { throw describeError(e); }
  }

  subscribe(onEvent: (e: RepoEvent) => void, onStatus: (s: LiveStatus) => void): () => void {
    onStatus('connecting');
    const channel = this.client
      .channel('a5w-sync')
      .on('postgres_changes', { event: '*', schema: 'public', table: 'bookings' }, (payload) => {
        try {
          if (payload.eventType !== 'DELETE') onEvent({ type: 'booking', booking: bookingFromRow(payload.new as BookingRow) });
        } catch (e) { console.warn('Événement temps réel ignoré', e); }
      })
      .on('postgres_changes', { event: '*', schema: 'public', table: 'contacts' }, (payload) => {
        try {
          if (payload.eventType === 'DELETE') {
            const old = payload.old as Partial<ContactRow>;
            if (old.id) onEvent({ type: 'contact-removed', id: old.id });
          } else {
            onEvent({ type: 'contact', contact: contactFromRow(payload.new as ContactRow) });
          }
        } catch (e) { console.warn('Événement temps réel ignoré', e); }
      })
      .on('postgres_changes', { event: '*', schema: 'public', table: 'profiles' }, (payload) => {
        try {
          if (payload.eventType !== 'DELETE') onEvent({ type: 'profile', profile: profileFromRow(payload.new as ProfileRow) });
        } catch (e) { console.warn('Événement temps réel ignoré', e); }
      })
      .subscribe((status) => {
        if (status === 'SUBSCRIBED') onStatus('live');
        else if (status === 'CHANNEL_ERROR' || status === 'TIMED_OUT' || status === 'CLOSED') onStatus('down');
        else onStatus('connecting');
      });
    return () => { void this.client.removeChannel(channel); };
  }
}
